import {
  buildConventionalScopeBody,
  ConventionalScope,
  conventionalScopeKey,
  coreApi,
  BLUEPRINT_FAMILY_KEEP_UNUSED_DATA_FOR_SECONDS,
  BlueprintFamilyNewGame,
  BlueprintFamilyTrendsWindowDays,
} from '../core'

// How the rollup is grouped. Maps to the BE `groupBy` query enum on
// POST /v2/blueprint-families/rollup. Note: an unknown enum value returns 404
// (a JAX-RS param-conversion quirk) — only send these two values.
export type BlueprintFamilyRollupGroupBy = 'EVENT_GROUP' | 'EVENT_TYPE'

// Single row of POST /v2/blueprint-families/rollup.
// Mirrors BE BlueprintFamilyRollupRespDto. With groupBy=EVENT_GROUP the rows are
// keyed by event group (e.g. groupName: "Monetization"); with groupBy=EVENT_TYPE
// they are keyed by event type (e.g. groupName: "Battle Pass").
//
// A row counts every game that ran ANY active event of the group or type, whether or
// not the event's blueprint belongs to a blueprint family (including events with no
// blueprint at all). That is the same population as the matching trends series, so
// `distinctGamesCount` equals the last point's `distinctGamesCount` of the matching
// `trends/event-groups` / `trends/event-types` item and `cohortGamesCount` equals its
// `trackedGamesCount`. `blueprintCount` is the exception: it counts the blueprint
// families seen on the row's active events, so it can be 0 while
// `distinctGamesCount` is above 0, and the reverse.
//
// `windowDays` picks the selected period (its last N complete calendar months, see
// `distinctGamesCount` on `BlueprintFamilyApiItem`). Both game counts, and so both
// `windowed` rates, cover the period's LAST month; `newGames` covers the whole period.
// `instantaneous` is the share of the cohort active on the period's last day.
export type BlueprintFamilyRollupApiItem = {
  groupId: string
  groupName: string
  blueprintCount: number
  distinctGamesCount: number
  adoptionRate: {
    windowed: number
    instantaneous: number
  }
  // Games whose FIRST-EVER event of this row, any active event, falls inside the
  // period, newest first; `[]` when none. Optional only for the transition.
  newGames?: BlueprintFamilyNewGame[]
  // The cohort every numerator on this row is counted over, and the denominator
  // behind `adoptionRate`: games tracked on or before the
  // period's first day. Identical to the list / detail / momentum
  // `cohortGamesCount` for the same request. Optional only for the transition — see
  // `resolveCohortSize`.
  cohortGamesCount?: number
  // Removed from the wire: the pre-cohort denominator, kept optional so
  // `resolveCohortSize` can still read it on environments that predate the change.
  // Delete with the rest of the transitional fallbacks.
  segmentGamesCount?: number
}

type GetBlueprintFamilyRollupQueryArgs = {
  groupBy?: BlueprintFamilyRollupGroupBy
  windowDays?: BlueprintFamilyTrendsWindowDays
} & ConventionalScope

const rollupApi = coreApi.injectEndpoints({
  endpoints: (builder) => ({
    getBlueprintFamilyRollup: builder.query<BlueprintFamilyRollupApiItem[], GetBlueprintFamilyRollupQueryArgs>({
      keepUnusedDataFor: BLUEPRINT_FAMILY_KEEP_UNUSED_DATA_FOR_SECONDS,
      query: ({ groupBy, windowDays, ...scope }) => ({
        url: 'blueprint-families/rollup',
        method: 'POST',
        params: {
          ...(groupBy !== undefined ? { groupBy } : {}),
          ...(windowDays !== undefined ? { windowDays } : {}),
        },
        body: buildConventionalScopeBody(scope),
      }),
      providesTags: (_result, _err, { groupBy, windowDays, ...scope }) => [
        {
          type: 'BlueprintFamilies',
          id: `rollup/${groupBy ?? 'EVENT_GROUP'}/${windowDays ?? 'default'}/${conventionalScopeKey(scope)}`,
        },
      ],
    }),
  }),
})

export const { useGetBlueprintFamilyRollupQuery } = rollupApi
